interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const headers: Record<string, string> = {
    ...(options.body !== undefined && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  };

  const res = await fetch(`/api${path}`, {
    method: options.method || 'GET',
    headers,
    credentials: 'include',
    body: options.body instanceof FormData ? options.body : options.body !== undefined ? JSON.stringify(options.body) : undefined,
  }).catch(() => {
    throw new ApiError('Network error - please check your connection and try again', 0);
  });

  if (res.status === 401) {
    window.dispatchEvent(new Event('nes:unauthorized'));
  }

  if (!res.ok) {
    throw new ApiError(await readErrorMessage(res), res.status);
  }
  return res.json() as Promise<T>;
};

export const api = {
  get: <T = unknown>(path: string) => request<T>(path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T = unknown>(
    path: string,
    formData: FormData,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ) => uploadWithProgress<T>(path, { method: 'POST', body: formData, onProgress, signal }),
  uploadPut: <T = unknown>(
    path: string,
    formData: FormData,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ) => uploadWithProgress<T>(path, { method: 'PUT', body: formData, onProgress, signal }),
};

/**
 * Turn a failed response into the most useful message available.
 *
 * The order matters. The API returns JSON with a `message` written for the
 * user, so that is preferred. Reverse proxies (LiteSpeed/nginx) answer with an
 * HTML error page, which is why the plain-text/HTML fallback exists — otherwise
 * a 502 from the proxy reaches the user as "[object Object]".
 */
const readErrorMessage = async (res: Response): Promise<string> => {
  let text = '';
  try {
    text = await res.text();
  } catch {
    /* body already consumed or unreadable */
  }

  if (text) {
    try {
      const data = JSON.parse(text);
      if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim();
      if (data && typeof data.error === 'string' && data.error.trim()) return data.error.trim();
    } catch {
      /* not JSON */
    }

    // An HTML error page: strip the tags and keep the heading text.
    const stripped = text
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (stripped && !/^<(!doctype|html)/i.test(text.trim())) return stripped.slice(0, 200);
  }

  return statusFallbackMessage(res.status);
};

/** Last-resort text when the response carried no usable message. */
const statusFallbackMessage = (status: number): string => {
  switch (status) {
    case 400: return 'The server rejected this request as invalid. Check the fields and try again.';
    case 401: return 'Your session has expired. Please sign in again.';
    case 403: return 'You do not have permission to do that.';
    case 404: return 'That item no longer exists. Refresh the page and try again.';
    case 413: return 'The upload is too large. Upload fewer or smaller files.';
    case 429: return 'Too many requests. Wait a moment and try again.';
    case 500: return 'Something went wrong on the server. Nothing was saved — please try again.';
    case 502:
    case 503:
    case 504:
      return 'The server is temporarily unavailable. Wait a few seconds and try again.';
    default:
      return status === 0
        ? 'Could not reach the server. Check your connection and try again.'
        : `Request failed (${status})`;
  }
};

interface UploadOptions {
  method?: string;
  body?: FormData;
  onProgress?: (percent: number) => void;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

const uploadWithProgress = <T>(path: string, options: UploadOptions): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'POST', `/api${path}`);
    xhr.withCredentials = true;

    Object.entries(options.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener('abort', onAbort);
      fn();
    };

    const onAbort = () => {
      xhr.abort();
      const err = new ApiError('Upload cancelled.', 0);
      err.name = 'AbortError';
      finish(() => reject(err));
    };
    options.signal?.addEventListener('abort', onAbort);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && options.onProgress) {
        // Cap at 99: the last percent is the server writing files and inserting
        // database rows, which XHR cannot observe.
        options.onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };

    xhr.timeout = UPLOAD_TIMEOUT_MS;

    xhr.ontimeout = () => {
      finish(() => reject(new ApiError(
        'The upload timed out before it finished. This usually means the connection dropped — check your network and try again.',
        0
      )));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (options.onProgress) options.onProgress(100);
        let parsed: T;
        try {
          parsed = (xhr.responseText ? JSON.parse(xhr.responseText) : {}) as T;
        } catch {
          parsed = {} as T;
        }
        finish(() => resolve(parsed));
        return;
      }
      if (xhr.status === 401) {
        window.dispatchEvent(new Event('nes:unauthorized'));
      }
      // Reuse the same message extraction as the JSON path so a proxy HTML
      // error page still produces something the user can act on.
      finish(() => reject(new ApiError(messageFromStatus(xhr.status, xhr.responseText), xhr.status)));
    };

    xhr.onerror = () => {
      finish(() => reject(new ApiError(
        'The connection dropped during the upload, so it did not complete. Check your internet connection and try again.',
        xhr.status || 0
      )));
    };

    xhr.onabort = () => {
      finish(() => reject(new ApiError('Upload cancelled.', 0)));
    };

    xhr.send(options.body);
  });

/**
 * Synchronous counterpart of `readErrorMessage` for XHR, where the body has
 * already been read into a string.
 */
const messageFromStatus = (status: number, body: string): string => {
  if (body) {
    try {
      const data = JSON.parse(body);
      if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim();
      if (data && typeof data.error === 'string' && data.error.trim()) return data.error.trim();
    } catch {
      /* not JSON */
    }
    const stripped = body
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (stripped && !/^<(!doctype|html)/i.test(body.trim())) return stripped.slice(0, 200);
  }
  return statusFallbackMessage(status);
};

export const buildQuery = (params: Record<string, string | number | boolean | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};
